import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { EM_DASH } from "@/lib/utils";

import { openIncident } from "../manufacturing/qcIncidentsApi";
import { linkQualityRca } from "./qualityRcaApi";
import { qualityNcrLabel } from "./qualityEventNcrApi";
import { QualityNcrDeviceWorkPanel } from "./QualityNcrDeviceWork";
import type { RcaDeviceAutomaticLog } from "./QualityRcaWorksheet";
import type { RcaDeviceWork } from "./rcaForm";

/**
 * Device details from the RCA worksheet. The investigation fields and photos live on the
 * device's NCR; this modal hosts that NCR worksheet and offers to create the NCR when the
 * device does not have one yet.
 */
export function QualityRcaDeviceModal({
  serial,
  qualityIncidentId,
  legacyWork,
  automaticLog,
  rcaId,
  writesAllowed,
  onClose,
  onNcrCreated,
  onWorkSaved,
}: {
  serial: string | null;
  qualityIncidentId: number | null;
  /** Legacy quality.rca.form.devices entry, used to seed the NCR worksheet until it is saved once. */
  legacyWork: RcaDeviceWork | null;
  automaticLog: RcaDeviceAutomaticLog | null;
  rcaId: number;
  writesAllowed: boolean;
  onClose: () => void;
  onNcrCreated: () => Promise<void> | void;
  onWorkSaved: (serial: string, work: RcaDeviceWork) => void;
}) {
  if (serial == null) return null;

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="rca-device-title">
      <div className="my-4 w-full max-w-5xl space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="rca-device-title" className="font-mono text-lg font-semibold text-slate-900">
              {serial}
            </h2>
            {qualityIncidentId != null ? (
              <p className="text-sm text-slate-600">
                Worksheet stored on{" "}
                <Link className="text-sky-700 hover:underline" to={`/quality/ncrs/${qualityIncidentId}`}>
                  {qualityNcrLabel(qualityIncidentId)}
                </Link>
                .
              </p>
            ) : null}
          </div>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Recorded on this device</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-slate-600">
              Received at SGS and logged in the RMA database are recorded automatically on this device. They do not show on the customer form.
            </p>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-slate-500">
                  <th className="py-1 pr-3 font-medium">Stage</th>
                  <th className="py-1 pr-3 font-medium">Date</th>
                  <th className="py-1 font-medium">Person</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100">
                  <td className="py-2 pr-3">Received at SGS (intake)</td>
                  <td className="py-2 pr-3">{automaticLog?.receivedAt || EM_DASH}</td>
                  <td className="py-2">{automaticLog?.loggedBy || EM_DASH}</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-2 pr-3">Logged in RMA database</td>
                  <td className="py-2 pr-3">{automaticLog?.loggedAt || EM_DASH}</td>
                  <td className="py-2">{automaticLog?.loggedBy || EM_DASH}</td>
                </tr>
              </tbody>
            </table>
          </CardBody>
        </Card>
        {qualityIncidentId != null ? (
          <QualityNcrDeviceWorkPanel
            key={qualityIncidentId}
            ncrId={qualityIncidentId}
            seed={legacyWork}
            writesAllowed={writesAllowed}
            onSaved={(work) => onWorkSaved(serial, work)}
          />
        ) : (
          <CreateNcrCard serial={serial} rcaId={rcaId} writesAllowed={writesAllowed} onCreated={onNcrCreated} />
        )}
      </div>
    </div>,
    document.body,
  );
}

function CreateNcrCard({
  serial,
  rcaId,
  writesAllowed,
  onCreated,
}: {
  serial: string;
  rcaId: number;
  writesAllowed: boolean;
  onCreated: () => Promise<void> | void;
}) {
  const [issue, setIssue] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => {
      const incident = await openIncident({ type: "device", serial, issue: issue.trim() });
      await linkQualityRca(rcaId, { qualityIncidentIds: [incident.id] });
      return incident.id;
    },
    onSuccess: async () => {
      setErrorMessage(null);
      await onCreated();
    },
    onError: (error: Error) => setErrorMessage(error.message || "Could not create the NCR."),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>No NCR for this device</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm text-slate-600">
          Device investigation fields and photos are recorded on the device's NCR. Create one to start the worksheet; it is
          linked to this RCA automatically. The device must be at SGS.
        </p>
        {writesAllowed ? (
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (issue.trim() === "") {
                setErrorMessage("Describe the issue.");
                return;
              }
              setErrorMessage(null);
              create.mutate();
            }}
          >
            <div>
              <Label htmlFor="rca-device-ncr-serial">Serial</Label>
              <Input id="rca-device-ncr-serial" value={serial} readOnly className="font-mono" />
            </div>
            <div>
              <Label htmlFor="rca-device-ncr-issue">Issue</Label>
              <TextArea
                id="rca-device-ncr-issue"
                value={issue}
                rows={3}
                onChange={(event) => setIssue(event.target.value)}
                className="font-sans"
              />
            </div>
            <Button type="submit" isLoading={create.isPending}>
              Create NCR
            </Button>
            {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}
          </form>
        ) : null}
      </CardBody>
    </Card>
  );
}
